import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid2,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  useTheme,
} from "@mui/material";
import PageTitle from "../../../../../views/components/common/pages/PageTitle";
import FormTitle from "../../../../../views/components/common/form/FormTitle";
import FormSmallTextField from "../../../../../views/components/common/form/FormTextField";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faUpload } from "@fortawesome/free-solid-svg-icons";
import { Poc } from "./Poc";
import { useStores } from "../../../use-stores";
import { useEffect, useRef, useState } from "react";
import { useNotification } from "../../../../../utils/NotificationsContext";
import { useLocation } from "react-router-dom";
import { observer } from "mobx-react";
import { vendor_type_list } from "../../../../../models/Vendors/VendorType";
import ImageUpload from "../../../../../views/components/common/upload/ImageUpload";
import ImageAPI from "../../../api/ImageAPI";
import { HelperAPI } from "../../../api/HelperAPI";
import CloseIcon from "@mui/icons-material/Close";
interface AddEditVendorProps {
  isModal?: boolean;
  onClose?: () => void;
}

const AddEditVendor: React.FC<AddEditVendorProps> = ({
  isModal = false,
  onClose,
}) => {
  const theme = useTheme();
  const { vendorStore } = useStores();
  const { showError, showSuccess } = useNotification();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const vendorId = queryParams.get("vendor-id");
  const [open, setOpen] = useState(false);
  let vendorTypeMap = new Map<number, string>();
  const [selectedImage, setSelectedImage] = useState<File[]>([]);
  const uploadingImageId = useRef("");
  const [loading, setLoading] = useState(false);
  const [catalogInputType, setCatalogInputType] = useState<"upload" | "link">(
    "upload",
  );

  const handleOpen = (id: string) => {
    uploadingImageId.current = id;
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (vendorId) {
      vendor_type_list.forEach((type) => {
        vendorTypeMap.set(Number(type.type_id), type.type);
      });
      vendorStore
        .getVendorById(Number(vendorId))
        .then(() => {
          if (
            vendorStore.vendorDetails?.types &&
            vendorStore.vendorDetails.types.length > 0
          ) {
            vendorStore.vendorDetails.types.forEach((vendorType) => {
              vendor_type_list.forEach((type) => {
                if (vendorType.type_id! === Number(type.type_id)) {
                  type.isChecked = true;
                }
              });
            });
          }

          if (vendorStore.vendorDetails?.msme_link) {
            vendorStore.updateVendorDetails("hasMSME", "yes");
          } else if (vendorStore.vendorDetails?.msme_declaration_link) {
            vendorStore.updateVendorDetails("hasMSME", "no");
          } else {
            vendorStore.updateVendorDetails("hasMSME", "no");
          }
          if (vendorStore.vendorDetails?.products_catalog_link) {
            vendorStore.updateVendorDetails("hasCatalog", "yes");
            const isUrl =
              vendorStore.vendorDetails.products_catalog_link.startsWith(
                "http",
              );
            setCatalogInputType(isUrl ? "link" : "upload");
          } else {
            vendorStore.updateVendorDetails("hasCatalog", "no");
          }
          if (vendorStore.vendorDetails?.supplier_type) {
            const normalizedSupplierType =
              vendorStore.vendorDetails.supplier_type.toLowerCase();
            vendorStore.updateVendorDetails(
              "supplier_type",
              normalizedSupplierType,
            );
          }
          if (
            vendorStore.vendorDetails?.poc_list &&
            vendorStore.vendorDetails.poc_list.length > 0
          ) {
            vendorStore.vendorDetails.poc_list =
              vendorStore.vendorDetails.poc_list.map((poc) => {
                const isPrimary =
                  poc.is_primary === "1" || String(poc.is_primary) === "1";
                const { is_primary, ...restPoc } = poc;
                return {
                  ...restPoc,
                  is_primary: isPrimary ? "1" : "0",
                };
              });
          }
        })
        .catch((err) =>
          showError(
            "Error occurred",
            err?.response?.data?.message ||
              "Something went wrong! Please try again later.",
          ),
        );
    } else {
      vendorStore.vendorDetails = {};
      if (!vendorStore.vendorDetails?.address?.country) {
        vendorStore.updateVendorAddress("country", "India");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId]);

  const isValidGSTIN = (value: string): boolean => {
    const trimmed = (value || "").trim().toUpperCase();
    if (trimmed.length !== 15) return false;
    return /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z]Z[0-9A-Z]$/i.test(trimmed);
  };
  const updateVendorDetails = (name: string, value: string) => {
    vendorStore.updateVendorDetails(name, value);

    if (name === "gst_number" && value && value.length >= 12) {
      const panNumber = value.substring(2, 12).toUpperCase();
      if (/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(panNumber)) {
        vendorStore.updateVendorDetails("pan_number", panNumber);
      }
    }
  };

  const updateVendorAddress = (name: string, value: string) => {
    vendorStore.updateVendorAddress(name, value);
  };

  const handleVendorType = (typeId: number) => {
    vendor_type_list.forEach((type) => {
      if (type.type_id === typeId) {
        type.isChecked = !type.isChecked;
      }
    });

    vendorStore.updateVendorType(typeId);
  };

  const handleDownloadLink = (url: string, defaultFilename: string | null) => {
    if (!url?.trim()) return;
    HelperAPI.downloadVendorFiles(url, defaultFilename).catch(
      (err: unknown) => {
        showError(
          "Download failed",
          (err as { response?: { data?: { message?: string } } })?.response
            ?.data?.message || "Failed to download file",
        );
      },
    );
  };

  const updateBankDetails = (name: string, value: string) => {
    if (vendorStore.vendorDetails?.bank_details) {
      vendorStore.vendorDetails.bank_details = {
        ...vendorStore.vendorDetails.bank_details,
        [name]: value,
      };
    } else {
      vendorStore.vendorDetails = {
        ...vendorStore.vendorDetails,
        bank_details: {
          [name]: value,
        },
      };
    }
  };

  const handleUpload = () => {
    setLoading(true);
    if (selectedImage.length > 0) {
      const imageAPI = new ImageAPI();
      imageAPI
        .uploadVendorDocuments(selectedImage, "vendor-documents")
        .then((uploadedUrl) => {
          vendorStore.updateVendorDetails(
            uploadingImageId.current,
            uploadedUrl[0],
          );
          handleClose();
          setSelectedImage([]);
          setLoading(false);
          showSuccess("Document uploaded successfully");
        })
        .catch((err) => {
          setLoading(false);
          showError(
            "Error occurred",
            err?.response?.data?.message ||
              "Something went wrong! Please try again later.",
          );
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="xl">
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <PageTitle title={vendorId ? "Edit Vendor" : "Add Vendor"} />
        {isModal && (
          <IconButton
            sx={{
              color: theme.palette.text.primary,
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
              },
            }}
            size="small"
            onClick={onClose}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      <Box sx={{ flexGrow: 1, mx: "auto" }}>
        <Card
          sx={{
            mb: 2,
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ mb: 3 }}>
              <CustomTypography
                variant="heading5"
                fontWeight={700}
                sx={{ color: theme.palette.primary.main, mb: 0.5 }}
              >
                Business Details
              </CustomTypography>
              <Divider sx={{ mt: 1 }} />
            </Box>
            <Grid2 container spacing={2}>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <FormTitle title="Name *" marginBottom={0} />
                <FormSmallTextField
                  label="Enter vendor name"
                  variant="outlined"
                  fontSize="14px"
                  id="name"
                  value={vendorStore.vendorDetails?.name || ""}
                  onChange={(id, value) => updateVendorDetails(id!, value!)}
                  sx={{ mb: 0 }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <FormTitle title="Email *" marginBottom={0} />
                <FormSmallTextField
                  label="Enter vendor email"
                  variant="outlined"
                  color="secondary"
                  type="email"
                  fontSize="14px"
                  id="email"
                  value={vendorStore.vendorDetails?.email || ""}
                  onChange={(id, value) => updateVendorDetails(id!, value!)}
                  disabled={!!vendorId}
                  sx={{ mb: 0 }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <FormTitle title="GST Number *" marginBottom={0} />
                <FormSmallTextField
                  label="Enter GST number"
                  variant="outlined"
                  color="secondary"
                  type="name"
                  fontSize="14px"
                  id="gst_number"
                  value={vendorStore.vendorDetails?.gst_number || ""}
                  onChange={(id, value) => updateVendorDetails(id!, value!)}
                  error={
                    !!(
                      vendorStore.vendorDetails?.gst_number?.trim() &&
                      !isValidGSTIN(vendorStore.vendorDetails.gst_number)
                    )
                  }
                  errorMessage="Invalid GST number. Must be 15 characters (e.g. 22AABCU9603R1ZX)"
                  sx={{ mb: 0 }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <FormTitle title="PAN Number *" marginBottom={0} />
                <FormSmallTextField
                  label="PAN number (auto-filled)"
                  variant="outlined"
                  color="secondary"
                  type="name"
                  fontSize="14px"
                  id="pan_number"
                  value={vendorStore.vendorDetails?.pan_number || ""}
                  onChange={(id, value) => updateVendorDetails(id!, value!)}
                  disabled
                  sx={{ mb: 0 }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <FormTitle title="Has MSME Certificate *" marginBottom={0} />
                <RadioGroup
                  row
                  value={vendorStore.vendorDetails?.hasMSME || ""}
                  onChange={(e) => {
                    vendorStore.updateVendorDetails("hasMSME", e.target.value);
                    if (e.target.value === "no") {
                      vendorStore.updateVendorDetails("msme_link", "");
                    } else if (e.target.value === "yes") {
                      vendorStore.updateVendorDetails(
                        "msme_declaration_link",
                        "",
                      );
                    }
                  }}
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontWeight: 500,
                      fontSize: "0.9rem",
                    },
                  }}
                >
                  <FormControlLabel
                    value="yes"
                    control={<Radio size="small" />}
                    label="Yes"
                  />
                  <FormControlLabel
                    value="no"
                    control={<Radio size="small" />}
                    label="No"
                  />
                </RadioGroup>
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <FormTitle title="Has Product Catalog *" marginBottom={0} />
                <RadioGroup
                  row
                  value={vendorStore.vendorDetails?.hasCatalog || ""}
                  onChange={(e) => {
                    vendorStore.updateVendorDetails(
                      "hasCatalog",
                      e.target.value,
                    );
                    if (e.target.value === "no") {
                      vendorStore.updateVendorDetails(
                        "products_catalog_link",
                        "",
                      );
                      setCatalogInputType("upload");
                    }
                  }}
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontWeight: 500,
                      fontSize: "0.9rem",
                    },
                  }}
                >
                  <FormControlLabel
                    value="yes"
                    control={<Radio size="small" />}
                    label="Yes"
                  />
                  <FormControlLabel
                    value="no"
                    control={<Radio size="small" />}
                    label="No"
                  />
                </RadioGroup>
              </Grid2>
            </Grid2>

            {vendorStore.vendorDetails?.hasMSME === "yes" && (
              <Grid2 container spacing={2} sx={{ mt: 1 }}>
                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <FormTitle title="MSME Certificate *" marginBottom={0} />
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1.5,
                      alignItems: "center",
                      mt: 1,
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => handleOpen("msme_link")}
                      sx={{
                        backgroundColor: theme.palette.primary.main,
                        color: "white",
                        "&:hover": {
                          backgroundColor: theme.palette.primary.dark,
                        },
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faUpload}
                        style={{ fontSize: "14px" }}
                      />
                    </IconButton>
                    {vendorStore.vendorDetails?.msme_link && (
                      <>
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleDownloadLink(
                              vendorStore.vendorDetails?.msme_link!,
                              vendorStore.vendorDetails
                                ? `MSME_${vendorStore.vendorDetails.id}.pdf`
                                : null,
                            )
                          }
                          sx={{
                            backgroundColor: theme.palette.success.main,
                            color: "white",
                            "&:hover": {
                              backgroundColor: theme.palette.success.dark,
                            },
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faDownload}
                            style={{ fontSize: "14px" }}
                          />
                        </IconButton>
                        <CustomTypography
                          variant="body2"
                          color="success.main"
                          sx={{ ml: 1 }}
                        >
                          Uploaded
                        </CustomTypography>
                      </>
                    )}
                  </Box>
                </Grid2>
              </Grid2>
            )}

            {vendorStore.vendorDetails?.hasMSME === "no" && (
              <Grid2 container spacing={2} sx={{ mt: 1 }}>
                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <FormTitle title="MSME Declaration Form" marginBottom={0} />
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1.5,
                      alignItems: "center",
                      mt: 1,
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => handleOpen("msme_declaration_link")}
                      sx={{
                        backgroundColor: theme.palette.primary.main,
                        color: "white",
                        "&:hover": {
                          backgroundColor: theme.palette.primary.dark,
                        },
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faUpload}
                        style={{ fontSize: "14px" }}
                      />
                    </IconButton>
                    {vendorStore.vendorDetails?.msme_declaration_link && (
                      <>
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleDownloadLink(
                              vendorStore.vendorDetails?.msme_declaration_link!,
                              vendorStore.vendorDetails
                                ? `MSME_Declaration_${vendorStore.vendorDetails.id}.pdf`
                                : null,
                            )
                          }
                          sx={{
                            backgroundColor: theme.palette.success.main,
                            color: "white",
                            "&:hover": {
                              backgroundColor: theme.palette.success.dark,
                            },
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faDownload}
                            style={{ fontSize: "14px" }}
                          />
                        </IconButton>
                        <CustomTypography
                          variant="body2"
                          color="success.main"
                          sx={{ ml: 1 }}
                        >
                          Uploaded
                        </CustomTypography>
                      </>
                    )}
                  </Box>
                </Grid2>
              </Grid2>
            )}

            {vendorStore.vendorDetails?.hasCatalog === "yes" && (
              <Grid2 container spacing={2} sx={{ mt: 1 }}>
                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <FormTitle title="Catalog Type *" marginBottom={0} />
                  <FormControl fullWidth size="small">
                    <InputLabel id="catalog-type" sx={{ fontSize: "14px" }}>
                      Select Type
                    </InputLabel>
                    <Select
                      labelId="catalog-type"
                      id="catalog-type-select"
                      value={catalogInputType}
                      label="Select Type"
                      onChange={(e) => {
                        setCatalogInputType(
                          e.target.value as "upload" | "link",
                        );
                        vendorStore.updateVendorDetails(
                          "products_catalog_link",
                          "",
                        );
                      }}
                      sx={{ height: "38px" }}
                    >
                      <MenuItem value="upload">Upload Document</MenuItem>
                      <MenuItem value="link">External Link</MenuItem>
                    </Select>
                  </FormControl>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <FormTitle title="Product Catalog *" marginBottom={0} />
                  {catalogInputType === "upload" ? (
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1.5,
                        alignItems: "center",
                        mt: 1,
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleOpen("products_catalog_link")}
                        sx={{
                          backgroundColor: theme.palette.primary.main,
                          color: "white",
                          "&:hover": {
                            backgroundColor: theme.palette.primary.dark,
                          },
                        }}
                      >
                        <FontAwesomeIcon
                          icon={faUpload}
                          style={{ fontSize: "14px" }}
                        />
                      </IconButton>
                      {vendorStore.vendorDetails?.products_catalog_link && (
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleDownloadLink(
                              vendorStore.vendorDetails?.products_catalog_link!,
                              vendorStore.vendorDetails
                                ? `Catalog_${vendorStore.vendorDetails.id}.pdf`
                                : null,
                            )
                          }
                          sx={{
                            backgroundColor: theme.palette.success.main,
                            color: "white",
                            "&:hover": {
                              backgroundColor: theme.palette.success.dark,
                            },
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faDownload}
                            style={{ fontSize: "14px" }}
                          />
                        </IconButton>
                      )}
                      {vendorStore.vendorDetails?.products_catalog_link && (
                        <CustomTypography
                          variant="body2"
                          color="success.main"
                          sx={{ ml: 1 }}
                        >
                          Uploaded
                        </CustomTypography>
                      )}
                    </Box>
                  ) : (
                    <FormSmallTextField
                      label="Enter catalog URL"
                      variant="outlined"
                      color="secondary"
                      type="url"
                      fontSize="14px"
                      id="products_catalog_link"
                      value={
                        vendorStore.vendorDetails?.products_catalog_link || ""
                      }
                      onChange={(id, value) => updateVendorDetails(id!, value!)}
                      sx={{ mb: 0 }}
                    />
                  )}
                </Grid2>
              </Grid2>
            )}

            <Grid2 container spacing={2} sx={{ mt: 1 }}>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <FormTitle title="Company Website Link" marginBottom={0} />
                <FormSmallTextField
                  label="Enter company website link"
                  variant="outlined"
                  color="secondary"
                  type="url"
                  fontSize="14px"
                  id="company_website_link"
                  value={vendorStore.vendorDetails?.company_website_link || ""}
                  onChange={(id, value) => updateVendorDetails(id!, value!)}
                  sx={{ mb: 0 }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <FormTitle title="Tracking URL" marginBottom={0} />
                <FormSmallTextField
                  label="Enter vendor tracking url"
                  variant="outlined"
                  color="secondary"
                  type="name"
                  fontSize="14px"
                  id="tracking_url"
                  value={vendorStore.vendorDetails?.tracking_url || ""}
                  onChange={(id, value) => updateVendorDetails(id!, value!)}
                  sx={{ mb: 0 }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <FormTitle title="Payment Term *" marginBottom={0} />
                <FormControl fullWidth size="small">
                  <InputLabel id="payment-term" sx={{ fontSize: "14px" }}>
                    Select Payment Term  (days)
                  </InputLabel>
                  <Select
                    labelId="payment-term"
                    id="payment-term-select"
                    value={
                      vendorStore.vendorDetails?.payment_term?.toString() || ""
                    }
                    label="Select Payment Term (days)"
                    onChange={(e) =>
                      updateVendorDetails("payment_term", e.target.value)
                    }
                    sx={{ height: "38px" }}
                  >
                    {(() => {
                      const standardOptions = [0,1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23,24,25,26,27,28,29,30, 45, 60];
                      const currentValue =
                        vendorStore.vendorDetails?.payment_term;
                      const currentValueNum = currentValue
                        ? Number(currentValue)
                        : null;
                      const options = [...standardOptions];
                      if (
                        vendorId &&
                        currentValueNum &&
                        !standardOptions.includes(currentValueNum)
                      ) {
                        options.push(currentValueNum);
                        options.sort((a, b) => a - b);
                      }

                      return options.map((days) => (
                        <MenuItem key={days} value={days.toString()}>
                          {days} days
                        </MenuItem>
                      ));
                    })()}
                  </Select>
                </FormControl>
              </Grid2>
            </Grid2>
          </CardContent>
        </Card>

        <Card
          sx={{
            mb: 3,
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ mb: 3 }}>
              <CustomTypography
                variant="heading5"
                fontWeight={700}
                sx={{ color: theme.palette.primary.main, mb: 0.5 }}
              >
                Address Details
              </CustomTypography>
              <Divider sx={{ mt: 1 }} />
            </Box>
            <Grid2 container spacing={2}>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <FormTitle title="Address Line 1 *" marginBottom={0} />
                <FormSmallTextField
                  label="Enter address line 1"
                  variant="outlined"
                  color="secondary"
                  type="name"
                  fontSize="14px"
                  id="address_line_1"
                  value={
                    vendorStore.vendorDetails?.address?.address_line_1 || ""
                  }
                  onChange={(id, value) => updateVendorAddress(id!, value!)}
                  sx={{ mb: 0 }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <FormTitle title="Address Line 2 *" marginBottom={0} />
                <FormSmallTextField
                  label="Enter address line 2"
                  variant="outlined"
                  color="secondary"
                  type="name"
                  fontSize="14px"
                  id="address_line_2"
                  value={
                    vendorStore.vendorDetails?.address?.address_line_2 || ""
                  }
                  onChange={(id, value) => updateVendorAddress(id!, value!)}
                  sx={{ mb: 0 }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <FormTitle title="Landmark" marginBottom={0} />
                <FormSmallTextField
                  label="Enter landmark"
                  variant="outlined"
                  color="secondary"
                  type="name"
                  fontSize="14px"
                  id="landmark"
                  value={vendorStore.vendorDetails?.address?.landmark || ""}
                  onChange={(id, value) => updateVendorAddress(id!, value!)}
                  sx={{ mb: 0 }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <FormTitle title="City *" marginBottom={0} />
                <FormSmallTextField
                  label="Enter city"
                  variant="outlined"
                  color="secondary"
                  type="name"
                  fontSize="14px"
                  id="city"
                  value={vendorStore.vendorDetails?.address?.city || ""}
                  onChange={(id, value) => updateVendorAddress(id!, value!)}
                  sx={{ mb: 0 }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <FormTitle title="State *" marginBottom={0} />
                <FormSmallTextField
                  label="Enter state"
                  variant="outlined"
                  color="secondary"
                  type="name"
                  fontSize="14px"
                  id="state"
                  value={vendorStore.vendorDetails?.address?.state || ""}
                  onChange={(id, value) => updateVendorAddress(id!, value!)}
                  sx={{ mb: 0 }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <FormTitle title="Country" marginBottom={0} />
                <FormSmallTextField
                  label="Country"
                  variant="outlined"
                  color="secondary"
                  type="name"
                  fontSize="14px"
                  id="country"
                  value={vendorStore.vendorDetails?.address?.country || "India"}
                  onChange={(id, value) =>
                    updateVendorAddress(id!, value || "India")
                  }
                  disabled
                  sx={{ mb: 0 }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <FormTitle title="Postal Code *" marginBottom={0} />
                <FormSmallTextField
                  label="Enter postal code"
                  variant="outlined"
                  color="secondary"
                  type="number"
                  fontSize="14px"
                  id="postal_code"
                  value={vendorStore.vendorDetails?.address?.postal_code || ""}
                  onChange={(id, value) => updateVendorAddress(id!, value!)}
                  sx={{ mb: 0 }}
                />
              </Grid2>
            </Grid2>
          </CardContent>
        </Card>

        <Card
          sx={{
            mb: 3,
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ mb: 3 }}>
              <CustomTypography
                variant="heading5"
                fontWeight={700}
                sx={{ color: theme.palette.primary.main, mb: 0.5 }}
              >
                Vendor Type & Classification
              </CustomTypography>
              <Divider sx={{ mt: 1 }} />
            </Box>

            <FormTitle title="Vendor Type *" marginBottom={0} />
            <Grid2 container spacing={2} sx={{ mt: 1, mb: 2 }}>
              {vendor_type_list.map((type, i) => (
                <Grid2 size={{ xs: 12, sm: 6, md: 4 }} key={i}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          vendorStore.vendorDetails &&
                          vendorStore.vendorDetails?.types
                            ? vendorStore.vendorDetails?.types?.findIndex(
                                (vendorType) =>
                                  vendorType.type_id === type.type_id,
                              ) !== -1
                            : false
                        }
                        onChange={() => {
                          handleVendorType(type.type_id);
                          if (type.type_id === 3) {
                            const isSelected =
                              vendorStore.vendorDetails?.types?.findIndex(
                                (vendorType) => vendorType.type_id === 3,
                              ) !== -1;
                            if (!isSelected) {
                              vendorStore.updateVendorDetails(
                                "supplier_type",
                                "",
                              );
                            }
                          }
                        }}
                        color="primary"
                      />
                    }
                    label={type.type}
                    sx={{
                      "& .MuiFormControlLabel-label": {
                        fontSize: "0.95rem",
                        fontWeight: 500,
                      },
                    }}
                  />
                </Grid2>
              ))}
            </Grid2>

            {vendorStore.vendorDetails?.types?.some(
              (type) => type.type_id === 3,
            ) && (
              <Grid2 container spacing={2} sx={{ mt: 2 }}>
                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <FormTitle title="Supplier Type *" marginBottom={0} />
                  <RadioGroup
                    row
                    value={vendorStore.vendorDetails?.supplier_type || ""}
                    onChange={(e) =>
                      vendorStore.updateVendorDetails(
                        "supplier_type",
                        e.target.value,
                      )
                    }
                    sx={{
                      "& .MuiFormControlLabel-label": {
                        fontWeight: 500,
                        fontSize: "0.9rem",
                      },
                    }}
                  >
                    <FormControlLabel
                      value="brand"
                      control={<Radio size="small" />}
                      label="Brand"
                    />
                    <FormControlLabel
                      value="distributor"
                      control={<Radio size="small" />}
                      label="Distributor"
                    />
                  </RadioGroup>
                </Grid2>
              </Grid2>
            )}
          </CardContent>
        </Card>

        <Card
          sx={{
            mb: 3,
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ mb: 3 }}>
              <CustomTypography
                variant="heading5"
                fontWeight={700}
                sx={{ color: theme.palette.primary.main, mb: 0.5 }}
              >
                Bank Account Details
              </CustomTypography>
              <Divider sx={{ mt: 1 }} />
            </Box>
            <Grid2 container spacing={2}>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <FormTitle title="Account Holder Name *" marginBottom={0} />
                <FormSmallTextField
                  label="Enter account holder name"
                  variant="outlined"
                  color="secondary"
                  type="name"
                  fontSize="14px"
                  id="account_holder_name"
                  value={
                    vendorStore.vendorDetails?.bank_details
                      ?.account_holder_name || ""
                  }
                  onChange={(id, value) => updateBankDetails(id!, value!)}
                  sx={{ mb: 0 }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <FormTitle title="Bank Name *" marginBottom={0} />
                <FormSmallTextField
                  label="Enter bank name"
                  variant="outlined"
                  color="secondary"
                  type="name"
                  fontSize="14px"
                  id="bank_name"
                  value={
                    vendorStore.vendorDetails?.bank_details?.bank_name || ""
                  }
                  onChange={(id, value) => updateBankDetails(id!, value!)}
                  sx={{ mb: 0 }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <FormTitle title="Branch Name *" marginBottom={0} />
                <FormSmallTextField
                  label="Enter branch name"
                  variant="outlined"
                  color="secondary"
                  type="name"
                  fontSize="14px"
                  id="branch_name"
                  value={
                    vendorStore.vendorDetails?.bank_details?.branch_name || ""
                  }
                  onChange={(id, value) => updateBankDetails(id!, value!)}
                  sx={{ mb: 0 }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <FormTitle title="Account Number *" marginBottom={0} />
                <FormSmallTextField
                  label="Enter account number"
                  variant="outlined"
                  color="secondary"
                  type="number"
                  fontSize="14px"
                  id="account_number"
                  value={
                    vendorStore.vendorDetails?.bank_details?.account_number ||
                    ""
                  }
                  onChange={(id, value) => updateBankDetails(id!, value!)}
                  sx={{ mb: 0 }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <FormTitle title="IFSC Code *" marginBottom={0} />
                <FormSmallTextField
                  label="Enter IFSC code"
                  variant="outlined"
                  color="secondary"
                  type="name"
                  fontSize="14px"
                  id="ifsc_code"
                  value={
                    vendorStore.vendorDetails?.bank_details?.ifsc_code || ""
                  }
                  onChange={(id, value) => {
                    const upperValue = value?.toUpperCase() || "";
                    updateBankDetails(id!, upperValue);
                  }}
                  sx={{ mb: 0 }}
                />
              </Grid2>
            </Grid2>
          </CardContent>
        </Card>

        <Card
          sx={{
            mb: 3,
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ mb: 3 }}>
              <CustomTypography
                variant="heading5"
                fontWeight={700}
                sx={{ color: theme.palette.primary.main, mb: 0.5 }}
              >
                Required Documents
              </CustomTypography>
              <Divider sx={{ mt: 1 }} />
            </Box>
            <Grid2 container spacing={3}>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <Box
                  sx={{
                    p: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: "8px",
                    backgroundColor: theme.palette.grey[50],
                    "&:hover": {
                      backgroundColor: theme.palette.grey[100],
                    },
                  }}
                >
                  <FormTitle title="PAN Card *" marginBottom={0} />
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1.5,
                      alignItems: "center",
                      mt: 1,
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => handleOpen("pan_card_link")}
                      sx={{
                        backgroundColor: theme.palette.primary.main,
                        color: "white",
                        "&:hover": {
                          backgroundColor: theme.palette.primary.dark,
                        },
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faUpload}
                        style={{ fontSize: "14px" }}
                      />
                    </IconButton>
                    {vendorStore.vendorDetails?.pan_card_link && (
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleDownloadLink(
                            vendorStore.vendorDetails?.pan_card_link!,
                            vendorStore.vendorDetails
                              ? `PAN_Card_${vendorStore.vendorDetails.id}.pdf`
                              : null,
                          )
                        }
                        sx={{
                          backgroundColor: theme.palette.success.main,
                          color: "white",
                          "&:hover": {
                            backgroundColor: theme.palette.success.dark,
                          },
                        }}
                      >
                        <FontAwesomeIcon
                          icon={faDownload}
                          style={{ fontSize: "14px" }}
                        />
                      </IconButton>
                    )}
                    {vendorStore.vendorDetails?.pan_card_link && (
                      <CustomTypography
                        variant="body2"
                        color="success.main"
                        sx={{ ml: 1 }}
                      >
                        Uploaded
                      </CustomTypography>
                    )}
                  </Box>
                </Box>
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <Box
                  sx={{
                    p: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: "8px",
                    backgroundColor: theme.palette.grey[50],
                    "&:hover": {
                      backgroundColor: theme.palette.grey[100],
                    },
                  }}
                >
                  <FormTitle title="GST Certificate *" marginBottom={0} />
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1.5,
                      alignItems: "center",
                      mt: 1,
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => handleOpen("gstin_certificate_link")}
                      sx={{
                        backgroundColor: theme.palette.primary.main,
                        color: "white",
                        "&:hover": {
                          backgroundColor: theme.palette.primary.dark,
                        },
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faUpload}
                        style={{ fontSize: "14px" }}
                      />
                    </IconButton>
                    {vendorStore.vendorDetails?.gstin_certificate_link && (
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleDownloadLink(
                            vendorStore.vendorDetails?.gstin_certificate_link!,
                            vendorStore.vendorDetails
                              ? `GST_Certificate_${vendorStore.vendorDetails.id}.pdf`
                              : null,
                          )
                        }
                        sx={{
                          backgroundColor: theme.palette.success.main,
                          color: "white",
                          "&:hover": {
                            backgroundColor: theme.palette.success.dark,
                          },
                        }}
                      >
                        <FontAwesomeIcon
                          icon={faDownload}
                          style={{ fontSize: "14px" }}
                        />
                      </IconButton>
                    )}
                    {vendorStore.vendorDetails?.gstin_certificate_link && (
                      <CustomTypography
                        variant="body2"
                        color="success.main"
                        sx={{ ml: 1 }}
                      >
                        Uploaded
                      </CustomTypography>
                    )}
                  </Box>
                </Box>
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <Box
                  sx={{
                    p: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: "8px",
                    backgroundColor: theme.palette.grey[50],
                    "&:hover": {
                      backgroundColor: theme.palette.grey[100],
                    },
                  }}
                >
                  <FormTitle title="Bank Details*" marginBottom={0} />
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1.5,
                      alignItems: "center",
                      mt: 1,
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => handleOpen("bank_details_link")}
                      sx={{
                        backgroundColor: theme.palette.primary.main,
                        color: "white",
                        "&:hover": {
                          backgroundColor: theme.palette.primary.dark,
                        },
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faUpload}
                        style={{ fontSize: "14px" }}
                      />
                    </IconButton>
                    {vendorStore.vendorDetails?.bank_details_link && (
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleDownloadLink(
                            vendorStore.vendorDetails?.bank_details_link!,
                            vendorStore.vendorDetails
                              ? `Bank_Details_${vendorStore.vendorDetails.id}.pdf`
                              : null,
                          )
                        }
                        sx={{
                          backgroundColor: theme.palette.success.main,
                          color: "white",
                          "&:hover": {
                            backgroundColor: theme.palette.success.dark,
                          },
                        }}
                      >
                        <FontAwesomeIcon
                          icon={faDownload}
                          style={{ fontSize: "14px" }}
                        />
                      </IconButton>
                    )}
                    {vendorStore.vendorDetails?.bank_details_link && (
                      <CustomTypography
                        variant="body2"
                        color="success.main"
                        sx={{ ml: 1 }}
                      >
                        Uploaded
                      </CustomTypography>
                    )}
                  </Box>
                </Box>
              </Grid2>
            </Grid2>
          </CardContent>
        </Card>

        <Card
          sx={{
            mb: 3,
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Poc
              pocs={vendorStore.vendorDetails?.poc_list || []}
              modal={isModal}
              onClose={onClose}
            />
          </CardContent>
        </Card>
      </Box>

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        slotProps={{
          paper: {
            sx: {
              borderRadius: "12px",
              boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
            },
          },
        }}
        sx={{ zIndex: 2000 }}
      >
        <DialogTitle
          sx={{
            pb: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <CustomTypography variant="heading5" fontWeight={600}>
            Upload Document
          </CustomTypography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <ImageUpload
            uploadText="Drop your Files here, or browse"
            showImagesList={true}
            existingImageList={[]}
            newImageList={selectedImage?.map((image) => image) || []}
            addImagesToBeUploaded={(file) => {
              setSelectedImage(file);
            }}
            removeImage={(image) => {
              const files = selectedImage.filter(
                (file) => file.name !== image.title,
              );
              setSelectedImage(files);
            }}
            size="large"
            multiple={false}
            allowedFileTypes="*/*"
          />
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            gap: 1,
          }}
        >
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              px: 3,
            }}
          >
            Cancel
          </Button>
          {loading ? (
            <Button
              variant="contained"
              disabled
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                px: 3,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 1,
              }}
            >
              <CircularProgress size={16} color="inherit" />
              Uploading...
            </Button>
          ) : (
            <Button
              onClick={() => handleUpload()}
              variant="contained"
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                px: 3,
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                "&:hover": {
                  boxShadow: "0 6px 16px rgba(0, 0, 0, 0.2)",
                },
              }}
            >
              Upload
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default observer(AddEditVendor);
